import {DARK_MODE_MEDIA_QUERY} from '@zzng/common/constants/mediaQuery';
import {ALL_SCHEME_REG, SCHEME} from '@zzng/common/constants/scheme';
import {TALK_VERSION} from '@zzng/common/constants/talkVersion';
import web2app from '@zzng/common/lib/web2app';
import {openExternalWebview} from '@zzng/common/scheme/web';
import {isValidTalkVersion, kakaoTalkVersion} from '@zzng/common/utils/agent';
import {appendQueryParamToUrl, log} from '@zzng/common/utils/common';
import {isServiceDomain} from '@zzng/common/utils/isServiceDomain';
import {determineProtocol, executeAppScheme, getIntent} from '@zzng/common/utils/scheme';

// ----------------------------------------------------------------------------
// 상수 정의
// ----------------------------------------------------------------------------

// 버전 정보
export const DIGITALCARD_MINIMUM_TALK_VERSION = TALK_VERSION['10.0.5']; // 디지털카드 2.0 전용웹뷰 최소 버전
export const IOS_HEADER_ISSUE_VERSION = TALK_VERSION['10.1.0']; // iOS 헤더 이슈 버전
export const STUDENTID_MINIMUM_TALK_VERSION = TALK_VERSION['10.2.0']; // 톡학생증 전용웹뷰 최소 버전
export const ZIKZANGID_MINIMUM_TALK_VERSION = TALK_VERSION['10.9.0']; // 톡사원증 전용웹뷰 최소 버전 (target=_blank 지원)
export const HOME_MINIMUM_TALK_VERSION = TALK_VERSION['11.2.0']; // 홈 관련 최소 버전

// 상태바 색상 정보
const COLOR = {
  DEFAULT_STATUS_BAR: '#999999',
  DARK_MODE: '#000000',
  LIGHT_MODE: '#ffffff'
} as const;

// ----------------------------------------------------------------------------
// 버전 체크 유틸리티
// ----------------------------------------------------------------------------

export const isRequiredKakaoTalkVersionForStudent = isValidTalkVersion(
  kakaoTalkVersion,
  STUDENTID_MINIMUM_TALK_VERSION
);

export const isRequiredKakaoTalkVersionForZikzang = isValidTalkVersion(
  kakaoTalkVersion,
  ZIKZANGID_MINIMUM_TALK_VERSION
);

export const isRequiredKakaoTalkVersionForHome = isValidTalkVersion(kakaoTalkVersion, HOME_MINIMUM_TALK_VERSION);

// ----------------------------------------------------------------------------
// 상태바 관련 함수
// ----------------------------------------------------------------------------

/**
 * 상태바 색상을 반환하는 함수
 * 앱은 ARGB, 웹은 RGBA로 반환한다.
 */
export const getStatusBarColor = async () => {
  const appColor = (await window.digitalCard?.getStatusBarColor?.()) ?? '#FFFFFFFF';
  const alpha = appColor.substring(1, 3);
  const rest = appColor.substring(3);
  const webColor = ['#', rest, alpha].join('');
  // 디바이스 별 상태바 색상
  return {app: appColor, web: webColor};
};

/**
 * 화면 상단의 웹뷰 영역 밖인 safe area를 딤드 처리하기 위한 인터페이스 (최소버전 10.2.0)
 */
export const changeStatusBarColor = (color: string = COLOR.DEFAULT_STATUS_BAR, duration = 0) => {
  try {
    window.digitalCard?.changeStatusBarColor?.(color, duration);
  } catch (error) {
    console.error(error);
  }

  // iOS 사파리 대응
  const metaSelectors = ["meta[name='apple-mobile-web-app-status-bar-style']", "meta[name='theme-color']"];

  metaSelectors
    .map((selector) => document.querySelector(selector))
    .filter(Boolean)
    .forEach((meta) => meta?.setAttribute('content', color));
};

/**
 * 다크모드 여부에 따라 상태바 색상을 초기화
 */
export const resetChangeStatusBarColor = () => {
  const isDarkMode = matchMedia(DARK_MODE_MEDIA_QUERY).matches;
  changeStatusBarColor(isDarkMode ? COLOR.DARK_MODE : COLOR.LIGHT_MODE);
};

// ----------------------------------------------------------------------------
// 디지털카드 스킴 관련 함수
// ----------------------------------------------------------------------------

/**
 * 디지털카드 스킴 URL 생성
 */
export const createOpenDigitalCardScheme = ({url, target}: {url: string; target?: '_blank'}) => {
  return appendQueryParamToUrl(SCHEME.DIGITAL_CARD, {url, target});
};

/**
 * 디지털카드 웹뷰 다 닫고 디지털카드 웹뷰 열기
 */
export const replaceDigitalCard = (url: string) => {
  const schemeUrl = appendQueryParamToUrl(url);
  executeAppScheme(createOpenDigitalCardScheme({url: schemeUrl}));
};

/**
 * 현재 웹뷰를 닫고 디지털카드 웹뷰 열기
 */
export const openDigitalCardAfterClose = (url: string) => {
  const schemeUrl = createOpenDigitalCardScheme({url, target: '_blank'});
  openExternalWebview(schemeUrl, {isCurrentWebviewClose: true});
};

/**
 * target=_blank를 사용하여 디지털카드 웹뷰 열기 (최소버전 10.9.0)
 * 기존 웹뷰를 유지한 채 새로운 디지털카드 웹뷰를 열 때 사용
 * https://jira.daumkakao.com/browse/CARD-493
 */
export const openDigitalCardOnTop = (url: string) => {
  if (!isServiceDomain(url)) {
    log('openDigitalCardOnTop 함수는 허용된 서비스 도메인만 사용할 수 있습니다.');
  }

  const schemeUrl = appendQueryParamToUrl(url);
  const scheme = createOpenDigitalCardScheme({url: schemeUrl, target: '_blank'});
  executeAppScheme(scheme);
};

/**
 * 외부 모바일웹 페이지에서 디지털카드 웹뷰를 실행
 * iOS 외부 모바일웹에서 사용자 인터렉션 없이 JavaScript로 웹뷰를 실행하는 경우
 * 유니버셜링크가 동작하지 않아 스킴을 직접 호출한다.
 */
export const openDigitalCardAtExternal = (url: string) => {
  const scheme = createOpenDigitalCardScheme({url});
  const protocol = determineProtocol();
  const urlScheme = scheme.replace(ALL_SCHEME_REG, protocol);
  const intentURI = getIntent(scheme);

  web2app({
    urlScheme,
    intentURI,
    onAppMissing: () => {}
  });
};

// ----------------------------------------------------------------------------
// 클라이언트 인터페이스 초기화
// ----------------------------------------------------------------------------

/**
 * 디지털카드 클라이언트 인터페이스 초기화
 * kakaotalk://digitalcard/open 스킴으로 들어온 웹뷰를 사용할 경우
 * DOM을 렌더링하기 전에 항상 호출해야 함
 */
export const initClientInterface = ({isIgnoredDarkMode}: {isIgnoredDarkMode?: boolean} = {}) => {
  window.historyBack = () => {
    if (isRequiredKakaoTalkVersionForHome) return undefined; // undefined는 history.back()을 의미
    return window.history.back();
  };

  window.handleMobileKeyboardStatus = () => {};

  isIgnoredDarkMode ? changeStatusBarColor(COLOR.LIGHT_MODE) : resetChangeStatusBarColor();
};
